import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import type { DateSelectProps } from "@/components/DateSelect/DateSelect";
import Dialog from "@/components/Dialog";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import SearchInput from "@/components/SearchInput";
import Select from "@/components/Select";
import Table, { buildActionColumn, TableControls } from "@/components/Table";
import TextField from "@/components/TextField";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import useTableRowMenu, { actionId, type TableRowMenu } from "@/hooks/useTableRowMenu";
import { filterByClientSearch } from "@/lib/clientSearch";
import { toFormat } from "@/lib/dateHelpers";
import { showSnackbar } from "@/lib/snackbarUtils";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import { useFlags } from "@/hooks/useFeatureFlags";
import TabLayout from "./_TabLayout";
import {
  CREATE_EDUCATION_CREDENTIAL_MUTATION,
  DELETE_EDUCATION_CREDENTIAL_MUTATION,
  UPDATE_EDUCATION_CREDENTIAL_MUTATION,
  type CreateEducationCredentialMutationResponse,
  type CreateEducationCredentialMutationVariables,
  type DeleteEducationCredentialMutationResponse,
  type DeleteEducationCredentialMutationVariables,
  type UpdateEducationCredentialMutationResponse,
  type UpdateEducationCredentialMutationVariables
} from "./mutations";
import {
  GET_EDUCATION_CREDENTIALS_QUERY,
  type EducationCredential,
  type EducationCredentialType,
  type EducationCredentialsQueryResponse
} from "./queries";
import { usePersonEducationAccess } from "../person/_usePersonEducationAccess";

type EducationCredentialRow = EducationCredential & {
  level: string;
};

type DialogState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; credential: EducationCredentialRow }
  | { mode: "delete"; credential: EducationCredentialRow };

type DegreeFormState = {
  description: string;
  educationType: EducationCredentialType | "";
  completedOn: string | null;
};

type DateRange = { startDate: string | null; endDate: string | null };

const educationTypeLabels: Record<EducationCredential["educationType"], string> = {
  ASSOCIATE_DEGREE: "2 year degree",
  BACHELORS_DEGREE: "4 year degree"
};

const educationTypeOptions = Object.entries(educationTypeLabels).map(([value, label]) => ({
  value,
  label
}));

const emptyDateRange: DateRange = { startDate: null, endDate: null };

const emptyDegreeForm: DegreeFormState = {
  description: "",
  educationType: "",
  completedOn: null
};

function formatLabel(
  value: EducationCredential["educationType"] | null | undefined,
  labels: Record<EducationCredential["educationType"], string>
) {
  if (!value) return "--";
  return labels[value] ?? value;
}

function formatDate(value: string | null | undefined) {
  return (value && toFormat(value)) || "--";
}

function formatCredentialRows(credentials: EducationCredential[] = []): EducationCredentialRow[] {
  return credentials.map((credential) => ({
    ...credential,
    level: formatLabel(credential.educationType, educationTypeLabels)
  }));
}

function getInitialFormState(credential?: EducationCredential): DegreeFormState {
  if (!credential) return emptyDegreeForm;

  return {
    description: credential.description ?? "",
    educationType: credential.educationType,
    completedOn: credential.completedOn ?? null
  };
}

function getColumns(
  hasData: boolean,
  setMenu: (menu: TableRowMenu) => void,
  onEdit: (credential: EducationCredentialRow) => void,
  onDelete: (credential: EducationCredentialRow) => void,
  canManageApplications: boolean
): ColumnDef<EducationCredentialRow>[] {
  const columns: ColumnDef<EducationCredentialRow>[] = [
    {
      header: "Description",
      accessorKey: "description",
      enableSorting: true,
      size: 300,
      sortingFn: "customAlphanumericSorting",
      cell: ({ row }) => row.original.description || "--",
      meta: {
        className: getStickyColumnClasses(hasData),
        headerClassName: "hide-header-in-column"
      }
    },
    {
      header: "Level",
      accessorKey: "level",
      enableSorting: true,
      size: 180,
      sortingFn: "customAlphanumericSorting"
    },
    {
      header: "Completion Date",
      accessorKey: "completedOn",
      enableSorting: true,
      size: 180,
      cell: ({ row }) => formatDate(row.original.completedOn)
    }
  ];

  if (!canManageApplications) return columns;

  return [
    ...columns,
    buildActionColumn<EducationCredentialRow>({
      onEditClick: (row) => onEdit(row),
      editButtonTitle: "Edit Degree",
      getActions: (row) => [
        { text: "Delete Degree", iconName: "delete", destructive: true, onClick: () => onDelete(row) }
      ],
      actionId,
      setMenu,
      getRowId: (row) => row.id
    })
  ];
}

function EducationCredentialDialog({
  open,
  mode,
  personId,
  pgaId,
  credential,
  onClose
}: {
  open: boolean;
  mode: "create" | "edit";
  personId?: string;
  pgaId: string;
  credential?: EducationCredential;
  onClose: () => void;
}) {
  const [form, setForm] = useState<DegreeFormState>(emptyDegreeForm);
  const [saveError, setSaveError] = useState<string | null>(null);
  const focusSinkRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (open) {
      setForm(getInitialFormState(mode === "edit" ? credential : undefined));
      setSaveError(null);
    }
  }, [credential, mode, open]);

  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => focusSinkRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  const refetchCredentials = { query: GET_EDUCATION_CREDENTIALS_QUERY, variables: { pgaId } };

  const [createCredential, { loading: creating }] = useMutation<
    CreateEducationCredentialMutationResponse,
    CreateEducationCredentialMutationVariables
  >(CREATE_EDUCATION_CREDENTIAL_MUTATION, {
    refetchQueries: [refetchCredentials],
    awaitRefetchQueries: true
  });

  const [updateCredential, { loading: updating }] = useMutation<
    UpdateEducationCredentialMutationResponse,
    UpdateEducationCredentialMutationVariables
  >(UPDATE_EDUCATION_CREDENTIAL_MUTATION, {
    refetchQueries: [refetchCredentials],
    awaitRefetchQueries: true
  });

  const completedOnRange: DateRange = form.completedOn
    ? { startDate: form.completedOn, endDate: form.completedOn }
    : emptyDateRange;
  const description = form.description.trim();
  const saving = creating || updating;
  const canSave = !!form.educationType && !!form.completedOn && !!description && !saving;
  const headline = mode === "create" ? "Add Degree" : "Edit Degree";

  const handleDateChange: DateSelectProps["onChange"] = (value) => {
    if (value && typeof value === "object") {
      const startDate = value.startDate instanceof Date ? value.startDate.toISOString().slice(0, 10) : value.startDate;
      setForm((current) => ({ ...current, completedOn: startDate ?? null }));
    } else {
      setForm((current) => ({ ...current, completedOn: null }));
    }
  };

  const handleSave = async () => {
    if (!form.educationType || !form.completedOn || !description) return;

    setSaveError(null);

    try {
      if (mode === "create") {
        if (!personId) {
          setSaveError("Unable to add degree because the person record is unavailable.");
          return;
        }

        const { data } = await createCredential({
          variables: {
            input: {
              personId,
              educationType: form.educationType,
              description,
              completedOn: form.completedOn
            }
          }
        });

        if (data?.createEducationCredential.success) {
          showSnackbar("Degree added");
          onClose();
        } else {
          setSaveError(data?.createEducationCredential.message ?? "Unable to add degree");
        }
      } else if (credential) {
        const { data } = await updateCredential({
          variables: {
            input: {
              id: credential.id,
              educationType: form.educationType,
              description,
              completedOn: form.completedOn
            }
          }
        });

        if (data?.updateEducationCredential.success) {
          showSnackbar("Degree updated");
          onClose();
        } else {
          setSaveError(data?.updateEducationCredential.message ?? "Unable to update degree");
        }
      }
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Unable to save degree");
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      headline={headline}
      onClosed={onClose}
      hasFormElements
      displayOverflow
      actions={
        <>
          <Button variant="text" onClick={onClose} testId="degree-dialog-cancel">
            Cancel
          </Button>
          <Button variant="filled" onClick={handleSave} disabled={!canSave} testId="degree-dialog-save">
            Save
          </Button>
        </>
      }
    >
      <div className="min-w-[360px] flex flex-col gap-6">
        <span ref={focusSinkRef} tabIndex={-1} aria-hidden="true" className="sr-only" />
        {saveError && <Banner variant="error" description={saveError} inline closable={false} hideSecondaryDismiss />}
        <TextField
          label="Description"
          value={form.description}
          onChangeText={(description) => setForm((current) => ({ ...current, description }))}
          required
        />
        <Select
          label="Level"
          value={form.educationType}
          options={educationTypeOptions}
          onSelect={(educationType) =>
            setForm((current) => ({ ...current, educationType: educationType as EducationCredentialType }))
          }
          required
          data-testid="education-type-select"
        />
        <DateSelect
          label="Completion Date"
          placeholder="Select Completion Date"
          value={completedOnRange}
          onChange={handleDateChange}
          useSingleDate
          useModal
          showFooter={false}
        />
      </div>
    </Dialog>
  );
}

function DeleteEducationCredentialDialog({
  open,
  credential,
  pgaId,
  onClose
}: {
  open: boolean;
  credential?: EducationCredential;
  pgaId: string;
  onClose: () => void;
}) {
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setDeleteError(null);
  }, [open]);

  const [deleteCredential, { loading }] = useMutation<
    DeleteEducationCredentialMutationResponse,
    DeleteEducationCredentialMutationVariables
  >(DELETE_EDUCATION_CREDENTIAL_MUTATION, {
    refetchQueries: [{ query: GET_EDUCATION_CREDENTIALS_QUERY, variables: { pgaId } }],
    awaitRefetchQueries: true
  });

  const handleConfirm = async () => {
    if (!credential) return;

    setDeleteError(null);

    try {
      const { data } = await deleteCredential({ variables: { input: { id: credential.id } } });

      if (data?.deleteEducationCredential.success) {
        showSnackbar("Degree deleted");
        onClose();
      } else {
        setDeleteError(data?.deleteEducationCredential.message ?? "Unable to delete degree");
      }
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : "Unable to delete degree");
    }
  };

  return (
    <Dialog
      open={open}
      headline="Delete Degree?"
      onClosed={onClose}
      actions={
        <>
          <Button variant="text" onClick={onClose} testId="delete-degree-cancel">
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            onClick={handleConfirm}
            disabled={loading || !credential}
            testId="delete-degree-confirm"
          >
            Delete
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {deleteError && (
          <Banner variant="error" description={deleteError} inline closable={false} hideSecondaryDismiss />
        )}
        <p className="text-body-medium">
          This will remove {credential?.description ? <strong>{credential.description}</strong> : "this degree"} from
          this person's academic degree history. This cannot be undone.
        </p>
      </div>
    </Dialog>
  );
}

export default function EducationCredentials() {
  const { pgaId } = useParams();
  const { loaded, canViewPersonEducation } = usePersonEducationAccess();
  const flags = useFlags();
  const academicDegreesEnabled = !!flags["backoffice-education-credentials"];
  const canManageApplications = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageApplications
  });
  const { Menu, setMenu } = useTableRowMenu();
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });
  const { data, loading, error, refetch } = useQuery<EducationCredentialsQueryResponse>(
    GET_EDUCATION_CREDENTIALS_QUERY,
    {
      variables: { pgaId: pgaId || "" },
      skip: !loaded || !canViewPersonEducation || !pgaId || !academicDegreesEnabled
    }
  );

  const { searchValue, setSearchValue, clearSearch } = useSearchWithParams({ paramName: "search" });
  const hasSearch = !!searchValue.trim();
  const tableData = useMemo(() => formatCredentialRows(data?.person.educationCredentials), [data]);
  const visibleRows = useMemo(
    () =>
      filterByClientSearch(tableData, searchValue, (row) => [row.description, row.level, formatDate(row.completedOn)]),
    [searchValue, tableData]
  );
  const columns = useMemo(
    () =>
      getColumns(
        tableData.length > 0,
        setMenu,
        (credential) => {
          setDialog({ mode: "edit", credential });
        },
        (credential) => {
          setDialog({ mode: "delete", credential });
        },
        canManageApplications
      ),
    [canManageApplications, setMenu, tableData.length]
  );

  if (!loaded) {
    return null;
  }

  if (!canViewPersonEducation) {
    return null;
  }

  if (!academicDegreesEnabled) {
    return <Navigate to={`/people/${pgaId}/education/professional-development`} replace />;
  }

  return (
    <TabLayout>
      <Menu />
      <div className="pt-12" />
      {error ? (
        <ErrorBoundary
          variant="inline"
          title="Unable to load academic degrees"
          message={error.message}
          action={
            <Button variant="outlined" onClick={() => refetch()} testId="education-credentials-retry">
              Retry
            </Button>
          }
        />
      ) : (
        <Table
          data={visibleRows}
          columns={columns}
          loading={loading}
          enableRowSelection={false}
          hasActiveFilters={hasSearch}
          renderControls={(_table, { onSortClick } = {}) => (
            <TableControls
              actions={
                canManageApplications
                  ? [
                      {
                        text: "Add Degree",
                        iconName: "add",
                        variant: "filled",
                        onClick: () => setDialog({ mode: "create" }),
                        testId: "add-degree"
                      }
                    ]
                  : []
              }
              onSortClick={onSortClick}
            >
              <SearchInput
                placeholder="Search Academic Degrees"
                query={searchValue}
                onChange={setSearchValue}
                onClear={clearSearch}
              />
            </TableControls>
          )}
          renderNoDataEmptyState={() => (
            <EmptyState
              title="No Academic Degrees"
              caption="There are no academic degrees for this person."
              iconName="school"
            />
          )}
          renderFilteredEmptyState={() => (
            <EmptyState title="No Results Found" caption="Try a different search." iconName="search" />
          )}
          data-testid="education-credentials-table"
        />
      )}
      <EducationCredentialDialog
        open={dialog.mode === "create" || dialog.mode === "edit"}
        mode={dialog.mode === "edit" ? "edit" : "create"}
        personId={data?.person.id}
        pgaId={pgaId || ""}
        credential={dialog.mode === "edit" ? dialog.credential : undefined}
        onClose={() => setDialog({ mode: "closed" })}
      />
      <DeleteEducationCredentialDialog
        open={dialog.mode === "delete"}
        pgaId={pgaId || ""}
        credential={dialog.mode === "delete" ? dialog.credential : undefined}
        onClose={() => setDialog({ mode: "closed" })}
      />
    </TabLayout>
  );
}
